import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import IntakePage, { SupervisorIntakePage } from "@/pages/IntakePage";
import LoginPage from "@/pages/auth/LoginPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import AcceptInvitePage from "@/pages/auth/AcceptInvitePage";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import OverviewPage from "@/pages/dashboard/OverviewPage";
import SubmissionsPage from "@/pages/dashboard/SubmissionsPage";
import SchedulePage from "@/pages/dashboard/SchedulePage";
import UsersPage from "@/pages/dashboard/UsersPage";
import AuditPage from "@/pages/dashboard/AuditPage";
import IntakeLinksPage from "@/pages/dashboard/IntakeLinksPage";
import EmailsPage from "@/pages/dashboard/EmailsPage";
import SettingsPage from "@/pages/dashboard/SettingsPage";
import AvailabilityPage from "@/pages/dashboard/AvailabilityPage";
import WhatsAppPage from "@/pages/dashboard/WhatsAppPage";
import { Loader2 } from "lucide-react";

function FullscreenLoader() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>
  );
}

function RequireAuth({ children, supervisorOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullscreenLoader />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (supervisorOnly && user.role !== "supervisor") return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/intake" replace />} />
            <Route path="/intake" element={<IntakePage />} />
            <Route
              path="/supervisorintake"
              element={
                <RequireAuth supervisorOnly>
                  <SupervisorIntakePage />
                </RequireAuth>
              }
            />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <DashboardLayout />
                </RequireAuth>
              }
            >
              <Route index element={<OverviewPage />} />
              <Route path="submissions" element={<SubmissionsPage />} />
              <Route path="submissions/:id" element={<SubmissionsPage />} />
              <Route path="schedule" element={<SchedulePage />} />
              <Route path="intake-links" element={<IntakeLinksPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route
                path="availability"
                element={
                  <RequireAuth supervisorOnly>
                    <AvailabilityPage />
                  </RequireAuth>
                }
              />
              <Route
                path="users"
                element={
                  <RequireAuth supervisorOnly>
                    <UsersPage />
                  </RequireAuth>
                }
              />
              <Route
                path="audit"
                element={
                  <RequireAuth supervisorOnly>
                    <AuditPage />
                  </RequireAuth>
                }
              />
              <Route
                path="emails"
                element={
                  <RequireAuth supervisorOnly>
                    <EmailsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="whatsapp"
                element={
                  <RequireAuth supervisorOnly>
                    <WhatsAppPage />
                  </RequireAuth>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/intake" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
      </AuthProvider>
    </div>
  );
}

export default App;
